/**
 * Sends page views to `POST /api/v1/activity/page-views`. Plain `fetch`
 * (not the API client), so the entry bundle stays free of the client and
 * its contracts; `keepalive` lets the last batch go out as the tab closes.
 * (`navigator.sendBeacon` cannot send the anti-CSRF header a signed-in
 * request needs.)
 */
import type { PageView, PageViewTransport } from './queue.js';

const VISIT_KEY = 'hire.visit';

/**
 * A random ID for this tab's visit, kept for the tab's life, so a signed-out
 * visit's page views can be grouped. The API stores only a peppered hash.
 */
export function visitId(): string {
  try {
    const existing = sessionStorage.getItem(VISIT_KEY);
    if (existing) return existing;
    const created = crypto.randomUUID();
    sessionStorage.setItem(VISIT_KEY, created);
    return created;
  } catch {
    return crypto.randomUUID();
  }
}

/**
 * The transport to the API.
 *
 * @param fetchFn - The fetch implementation (tests).
 */
export function apiTransport(fetchFn: typeof fetch = fetch): PageViewTransport {
  return async (batch: readonly PageView[], { beacon }) => {
    try {
      const response = await fetchFn('/api/v1/activity/page-views', {
        method: 'POST',
        headers: { 'content-type': 'application/json', rid: 'anti-csrf' },
        credentials: 'same-origin',
        keepalive: beacon,
        body: JSON.stringify({ visitId: visitId(), views: batch }),
      });
      // 4xx other than rate limiting will not succeed later: drop the batch.
      return (
        response.ok || (response.status >= 400 && response.status < 500 && response.status !== 429)
      );
    } catch {
      return false;
    }
  };
}
